import i18n from 'i18next';
import { call, put, takeLatest } from 'redux-saga/effects';

import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';

import { getErrorMessageFromAPI } from '../../utilz';
import { DeclineSpotBidEffect, updateShipmentExpiryDurationEffect } from '../auction/AuctionEffects';
import { setAppLoader } from '../common/CommonActions';
import {
  getQuoteMultiStopInError,
  getQuoteMultiStopInSuccess,
  getShipmentDocumentsListInError,
  getShipmentDocumentsListInSuccess,
  getShipmentProductDetailError,
  getShipmentProductDetailSuccess,
  getSpotBidExportSearchResultsError,
  getSpotBidExportSearchResultsSuccess,
  getSpotBidResultSubmitSuccess,
  getSpotBidSearchResultsError,
  getSpotBidSearchResultsSuccess,
  setSpotBidDeclineError,
  setSpotBidDeclineSuccess,
  setSpotBidSaveError,
  setSpotBidSaveSuccess,
  setTotalSpotBidCount,
} from './AuctionActions';
import {
  getQuoteMultiStopEffect,
  getShipmentDocumentListEffect,
  getShipmentProductDetailsEffect,
  getSpotBidSearchResultsEffect,
  submitSpotBidEffect,
} from './AuctionEffects';
import AuctionTypes from './AuctionTypes';

function* getSpotBidList({ payload }) {
  try {
    const { data } = yield call(getSpotBidSearchResultsEffect, payload);
    yield put(getSpotBidSearchResultsSuccess({ data: data?.totalData }));
    yield put(setTotalSpotBidCount({ totalCount: data?.count?.totalCount }));
  } catch (error) {
    yield put(
      getSpotBidSearchResultsError({
        message: getErrorMessageFromAPI(error),
      }),
    );
  }
}

function* getSpotBidListAfterInsert({ payload }) {
  try {
    const { data } = yield call(getSpotBidSearchResultsEffect, payload);
    yield put(getSpotBidResultSubmitSuccess({ data: data?.totalData }));
  } catch (error) {
    yield put(
      getSpotBidSearchResultsError({
        message: getErrorMessageFromAPI(error),
      }),
    );
  }
}

function* getSpotBidExportList({ payload }) {
  try {
    const { data } = yield call(getSpotBidSearchResultsEffect, payload);
    yield put(getSpotBidExportSearchResultsSuccess({ data: data?.totalData }));
  } catch (error) {
    yield put(
      getSpotBidExportSearchResultsError({
        message: getErrorMessageFromAPI(error),
      }),
    );
  }
}

function* getShipmentProductDetail({ payload }) {
  try {
    const { data } = yield call(getShipmentProductDetailsEffect, payload);
    yield put(getShipmentProductDetailSuccess({ data }));
  } catch (error) {
    yield put(
      getShipmentProductDetailError({
        message: getErrorMessageFromAPI(error),
      }),
    );
  }
}

function* getShipmentDocumentsList({ payload }) {
  try {
    const { data } = yield call(getShipmentDocumentListEffect, payload);

    yield put(getShipmentDocumentsListInSuccess({ data }));
  } catch (e) {
    yield put(
      getShipmentDocumentsListInError({
        message: getErrorMessageFromAPI(e),
      }),
    );
  }
}

function* getQuoteMultiStopList({ payload }) {
  try {
    const { data } = yield call(getQuoteMultiStopEffect, payload);

    yield put(getQuoteMultiStopInSuccess({ data }));
    yield put(setAppLoader(false));
  } catch (e) {
    yield put(
      getQuoteMultiStopInError({
        message: getErrorMessageFromAPI(e),
      }),
    );
    yield put(setAppLoader(false));
  }
}

function* saveSpotBid({ payload: { shouldExtendBidExpiry, ...payload } }) {
  try {
    const { data } = yield call(submitSpotBidEffect, payload);
    if (Number(data) === 1) {
      if (shouldExtendBidExpiry) {
        yield call(updateShipmentExpiryDurationEffect, { shipmentKey: payload.shipmentKey });
      }
      yield call(showNotification, {
        message: i18n.t('translation:default.notification.bidSubmitted'),
        type: NOTIFICATION_TYPES.SUCCESS,
      });
    } else {
      yield call(showNotification, {
        message: data?.[0].message || i18n.t('translation:default.notification.bidNotSubmitted'),
        type: NOTIFICATION_TYPES.ERROR,
      });
    }
    yield put(setSpotBidSaveSuccess({ data }));
  } catch (error) {
    yield call(showNotification, {
      message: getErrorMessageFromAPI(error) || i18n.t('translation:default.notification.bidNotSubmitted'),
      type: NOTIFICATION_TYPES.ERROR,
    });
    yield put(
      setSpotBidSaveError({
        message: getErrorMessageFromAPI(error),
      }),
    );
  }
}

function* declineSpotBid({ payload }) {
  try {
    const { data } = yield call(DeclineSpotBidEffect, payload);
    if (data) {
      yield call(showNotification, {
        message: i18n.t('translation:default.notification.bidDeclined'),
        type: NOTIFICATION_TYPES.SUCCESS,
      });
    } else {
      yield call(showNotification, {
        message: data?.[0].message || i18n.t('translation:default.notification.bidNotDeclined'),
        type: NOTIFICATION_TYPES.ERROR,
      });
    }
    yield put(setSpotBidDeclineSuccess({ data }));
  } catch (error) {
    yield call(showNotification, {
      message: getErrorMessageFromAPI(error) || i18n.t('translation:default.notification.bidNotDeclined'),
      type: NOTIFICATION_TYPES.ERROR,
    });
    yield put(
      setSpotBidDeclineError({
        message: getErrorMessageFromAPI(error),
      }),
    );
  }
}

export default function* AuctionSaga() {
  yield takeLatest(AuctionTypes.GET_SPOTBID_SEARCH_RESULTS_PROGRESS, getSpotBidList);
  yield takeLatest(AuctionTypes.GET_SPOTBID_EXPORT_SEARCH_RESULTS_PROGRESS, getSpotBidExportList);
  yield takeLatest(AuctionTypes.GET_DOCUMENTS_PROGRESS, getShipmentDocumentsList);
  yield takeLatest(AuctionTypes.GET_MULTI_QUOTE_PROGRESS, getQuoteMultiStopList);
  yield takeLatest(AuctionTypes.GET_SHIPMENT_PRODUCT_DETAIL_PROGRESS, getShipmentProductDetail);
  yield takeLatest(AuctionTypes.SET_SPOTBID_SAVE_PROGRESS, saveSpotBid);
  yield takeLatest(AuctionTypes.SET_SPOTBID_DECLINE_PROGRESS, declineSpotBid);
  yield takeLatest(AuctionTypes.GET_SPOTBID_RESULTS_SUBMIT_PROGRESS, getSpotBidListAfterInsert);
}
